import { useEffect, useMemo, useState } from "react";
import {
  createWithdraw,
  fetchWithdrawFeeQuote,
  fetchWithdrawTotp,
  type WalletItem,
} from "@/api/wallets";
import { errorMessage } from "@/api/auth";
import { ensureDeviceUnlock } from "@/security/deviceLock";
import { SelectField, TextField, StatusChip } from "@/ui/wx";
import { useToast } from "@/toast/ToastContext";

export function WithdrawCoin({
  token,
  wallets,
  usdtBalance,
  onClose,
  onDone,
}: {
  token: string;
  wallets: WalletItem[];
  usdtBalance: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [coin, setCoin] = useState("USDT");
  const [net, setNet] = useState("bsc");
  const [toAddr, setToAddr] = useState("");
  const [amount, setAmount] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [fee, setFee] = useState("—");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const bscFrom = useMemo(
    () => wallets.find((w) => String(w.chain).toLowerCase() === "bsc")?.address || "",
    [wallets]
  );

  useEffect(() => {
    fetchWithdrawTotp(token)
      .then((t) => setNeedTotp(!!(t.enabled || t.required)))
      .catch(() => setNeedTotp(false));
  }, [token]);

  useEffect(() => {
    const a = amount.trim();
    if (!a || Number(a) <= 0) {
      setFee("—");
      return;
    }
    const t = window.setTimeout(() => {
      fetchWithdrawFeeQuote(token, a)
        .then((q) => setFee(String(q.feeUsdt ?? q.totalUsdt ?? "—")))
        .catch(() => setFee("—"));
    }, 400);
    return () => clearTimeout(t);
  }, [amount, token]);

  async function onSubmit() {
    if (coin !== "USDT") {
      setStatus("فقط USDT");
      return;
    }
    if (!toAddr.trim() || !amount.trim()) {
      setStatus("ناقص");
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      await ensureDeviceUnlock();
      await createWithdraw(token, {
        toAddress: toAddr.trim(),
        amountUsdt: amount.trim().replace(/,/g, ""),
        fromAddress: bscFrom || undefined,
        totpCode: needTotp ? totp.trim() || undefined : undefined,
        idempotencyKey: `w-${Date.now()}`,
      });
      toast.ok("برداشت");
      onDone();
      onClose();
    } catch (e) {
      setStatus(errorMessage(e));
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="wx-flow" dir="rtl">
      <div className="row-between">
        <h2 className="wx-flow-title">برداشت کوین</h2>
        <button type="button" className="wx-chip" onClick={onClose}>
          ✕
        </button>
      </div>

      <SelectField
        label="کوین"
        value={coin}
        onChange={setCoin}
        options={[{ value: "USDT", label: "USDT" }]}
      />
      <SelectField
        label="شبکه"
        value={net}
        onChange={setNet}
        options={[{ value: "bsc", label: "BSC" }]}
      />

      <div className="wx-alert info">
        <span className="muted">موجودی</span>
        <b className="ltr">{usdtBalance.toLocaleString("en-US", { maximumFractionDigits: 4 })} USDT</b>
      </div>

      <TextField label="آدرس مقصد" value={toAddr} onChange={setToAddr} inputMode="text" />
      <TextField label="مبلغ" value={amount} onChange={setAmount} suffix="USDT" />

      <div className="row-between">
        <span className="muted">کارمزد</span>
        <span className="ltr">{fee}</span>
      </div>

      {needTotp && <TextField label="TOTP" value={totp} onChange={setTotp} inputMode="numeric" />}

      {status ? <StatusChip tone="warn">{status}</StatusChip> : null}

      <button
        type="button"
        className="wx-submit sell"
        disabled={busy || !bscFrom}
        onClick={() => void onSubmit()}
      >
        {busy ? "…" : "برداشت"}
      </button>
    </section>
  );
}
